/**
 * Which channels a task runs on (P-10.26), and what "Show it as" can pretend to be (P-2.10):
 * the seven channel words the sidecar uses (`ai-sidecar/src/channel.ts` `CHANNEL_KINDS`), in
 * the order the owner is shown them. Pure.
 */

export const TASK_CHANNELS = Object.freeze([
  'whatsapp',
  'instagram',
  'messenger',
  'telegram',
  'website',
  'email',
  'sms',
]);

/** The i18n key of each channel's own name. */
export const CHANNEL_NAME_KEY = Object.freeze({
  whatsapp: 'WASLO.CHANNEL_NAMES.WHATSAPP',
  instagram: 'WASLO.CHANNEL_NAMES.INSTAGRAM',
  messenger: 'WASLO.CHANNEL_NAMES.MESSENGER',
  telegram: 'WASLO.CHANNEL_NAMES.TELEGRAM',
  website: 'WASLO.CHANNEL_NAMES.WEBSITE',
  email: 'WASLO.CHANNEL_NAMES.EMAIL',
  sms: 'WASLO.CHANNEL_NAMES.SMS',
});

/**
 * Chatwoot's channel class to the channel word, as the sidecar maps it (`channelKindOf`), or
 * `null` for one Waslo does not name.
 */
const KIND_BY_CLASS = Object.freeze({
  'Channel::Whatsapp': 'whatsapp',
  'Channel::Instagram': 'instagram',
  'Channel::FacebookPage': 'messenger',
  'Channel::Telegram': 'telegram',
  'Channel::WebWidget': 'website',
  'Channel::Email': 'email',
  'Channel::Sms': 'sms',
  'Channel::TwilioSms': 'sms',
});

export const channelKindOf = channelType =>
  typeof channelType === 'string' &&
  Object.prototype.hasOwnProperty.call(KIND_BY_CLASS, channelType)
    ? KIND_BY_CLASS[channelType]
    : null;

/** The account's channels, in the order above, once each, from Chatwoot inbox rows. */
export const channelsOfInboxes = inboxes => {
  const found = new Set(
    (Array.isArray(inboxes) ? inboxes : [])
      .map(inbox => channelKindOf(inbox?.channel_type))
      .filter(Boolean)
  );
  return TASK_CHANNELS.filter(kind => found.has(kind));
};

/**
 * The channels of the account that this task does NOT run on, for the list row's "Not on
 * {channel}". `null` (every channel) is none.
 */
export const channelsNotOn = (taskChannels, accountChannels) => {
  if (!Array.isArray(taskChannels)) return [];
  return (accountChannels ?? []).filter(kind => !taskChannels.includes(kind));
};

/**
 * The channels a task may be limited to on the composer: the ones the account has, and any the
 * task is already limited to (a channel removed since stays visible, so it can be unticked).
 * With no account channels known, all seven.
 */
export const channelChoices = (accountChannels, taskChannels) => {
  const known = Array.isArray(accountChannels) && accountChannels.length > 0;
  const wanted = new Set([
    ...(known ? accountChannels : TASK_CHANNELS),
    ...(Array.isArray(taskChannels) ? taskChannels : []),
  ]);
  return TASK_CHANNELS.filter(kind => wanted.has(kind));
};
